import config from '../../config';

/**
 * Returns the onboarding link for the given onboarding parameters
 * @param {object} onboardingParameters The onboarding parameters
 * @param {string} onboardingParameters.coachId id of the coach
 * @param {string} onboardingParameters.plan The coach plan
 * @param {string} [onboardingParameters.email] The user's email to onboard
 * @param {string} [onboardingParameters.anonid] Segment anonymous id
 * @param {string} [onboardingParameters.startAt] billing start date
 * @returns {string} onboarding link
 */
const getOnboardingLink = ({
  coachId,
  plan,
  email,
  anonid,
  startAt,
}) => {
  const newURL = new URL(`${config.webAppURL}/onboarding`);
  newURL.searchParams.set('externalCoach', coachId);
  newURL.searchParams.set('plan', plan);

  if (email) {
    newURL.searchParams.set('email', email);
  }

  if (anonid) {
    newURL.searchParams.set('anonid', anonid);
  }

  if (startAt) {
    newURL.searchParams.set('startAt', startAt);
  }

  return newURL.toString();
};

/**
 * Get an onboarding link that uses the leadId for identifying the correct contract.
 * @param {string} leadId
 * @returns {string}
 */
const getOnboardingLinkFromLead = (leadId) => {
  const onboardingLink = new URL(`${config.webAppURL}/onboarding`);

  // Add the lead id as a query param
  onboardingLink.searchParams.set('leadId', leadId);

  return onboardingLink.toString();
};

/**
 * Get an onboarding link that uses a contract ID. This allows for reusable contracts that can be applied on challenges,
 * direct urchase or promo campains. Since we are using a contract, we can control the fees that are associated.
 *
 * When the link is sent to a known lead, pass their id too: the contract created at checkout then carries
 * the leadId, which is what files the sale under that lead and attributes the Inside Sales commission.
 * It does not move the lead's status — `changeLeadState` keys on an invoice's subscription, which a
 * one-time challenge purchase has none of (see system2-server#3129).
 *
 * @param {string} contractId
 * @param {string} [leadId] The lead the link is being sent to, when there is one.
 * @returns {string}
 */
const getOnboardingLinkFromContract = (contractId, leadId = '') => {
  const onboardingLink = new URL(`${config.webAppURL}/onboarding`);

  onboardingLink.searchParams.set('contractId', contractId);

  if (leadId) {
    onboardingLink.searchParams.set('leadId', leadId);
  }

  return onboardingLink.toString();
};

/**
 * Extracts the contractId from an onboarding link produced by getOnboardingLinkFromContract.
 * @param {string} url
 * @returns {string} The contractId, or '' if the url is missing/malformed.
 */
const getContractIdFromOnboardingLink = (url) => {
  if (!url) return '';
  try {
    return new URL(url).searchParams.get('contractId') || '';
  } catch {
    return '';
  }
};

export {
  getOnboardingLink,
  getOnboardingLinkFromLead,
  getOnboardingLinkFromContract,
  getContractIdFromOnboardingLink,
};
